import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Copy, Clock, History } from "lucide-react";

const STATUS_LABEL = { new: "New", in_progress: "In Progress", on_hold: "On Hold", completed: "Completed", cancelled: "Cancelled" };
const STATUS_COLOR = {
  new: "bg-slate-100 text-slate-800 border-slate-200",
  in_progress: "bg-amber-100 text-amber-800 border-amber-200",
  on_hold: "bg-purple-100 text-purple-800 border-purple-200",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  cancelled: "bg-slate-100 text-slate-500 border-slate-200",
};

export default function MyRequests({ api, onDuplicated }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [duplicating, setDuplicating] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${api}/work-orders/mine`);
      setOrders(r.data);
    } catch (e) {
      toast.error("Failed to load your requests");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const duplicate = async (o) => {
    setDuplicating(o.id);
    try {
      const r = await axios.post(`${api}/work-orders/${o.id}/duplicate`);
      toast.success(`Duplicated as ${r.data.ticket_no} — a fresh request was submitted`);
      load();
      onDuplicated?.();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to duplicate");
    } finally {
      setDuplicating(null);
    }
  };

  if (loading) return <div className="text-sm text-slate-500 py-6" data-testid="my-requests-loading">Loading your requests...</div>;

  return (
    <div className="space-y-3" data-testid="my-requests-panel">
      <p className="text-sm text-slate-600">
        Everything you've submitted. If the same issue happens again, hit <strong>Duplicate</strong> to file a fresh copy instantly.
      </p>
      {orders.length === 0 && (
        <div className="text-center py-10 text-slate-500 bg-white border border-dashed border-slate-200 rounded-lg" data-testid="my-requests-empty">
          You haven't submitted any work orders yet.
        </div>
      )}
      {orders.map((o) => (
        <Card key={o.id} className="border-slate-200 shadow-sm" data-testid={`my-order-${o.ticket_no}`}>
          <CardContent className="p-5">
            <div className="flex flex-col md:flex-row md:items-start gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center flex-wrap gap-2 mb-2">
                  <span className="font-mono text-xs text-slate-500">{o.ticket_no}</span>
                  <Badge className={`border ${STATUS_COLOR[o.status] || STATUS_COLOR.new}`} data-testid={`my-status-${o.ticket_no}`}>{STATUS_LABEL[o.status] || o.status}</Badge>
                  <Badge className="border bg-slate-50 text-slate-700 border-slate-200">{o.priority}</Badge>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs font-mono uppercase tracking-wider text-slate-500 mb-1.5">
                  <span className="bg-slate-900 text-white px-2 py-0.5 rounded">{o.property}</span>
                  <span className="text-slate-400">·</span>
                  <span>{o.issue_type}</span>
                </div>
                <div className="text-sm text-slate-800"><strong>{o.office_area}</strong> — {o.company_name}</div>
                <p className="text-sm text-slate-600 mt-1">{o.description}</p>
                <div className="mt-2 text-xs text-slate-500 flex flex-wrap gap-3 items-center">
                  <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />Due: {o.no_due_date ? "N/A" : (o.due_date || "N/A")}</span>
                  <span>Submitted {o.created_at ? new Date(o.created_at).toLocaleDateString() : ""}</span>
                  {o.status === "on_hold" && o.hold_notes && <span className="text-purple-700">On hold: {o.hold_notes}</span>}
                </div>
              </div>
              <div className="shrink-0">
                <Button onClick={() => duplicate(o)} disabled={duplicating === o.id} variant="outline" className="bg-white" data-testid={`duplicate-button-${o.ticket_no}`}>
                  <Copy className="w-4 h-4 mr-2" />{duplicating === o.id ? "Duplicating..." : "Duplicate"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
      {orders.length > 0 && (
        <div className="text-xs text-slate-400 font-mono inline-flex items-center gap-1.5 pt-1"><History className="w-3.5 h-3.5" />{orders.length} request(s) submitted by you</div>
      )}
    </div>
  );
}
